import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { imgProxy } from "@/lib/img";
import {
  ExternalLink, History, ChevronLeft, ChevronRight, Loader2, Flag, Bell,
} from "lucide-react";
import TimelineDialog, { TYPE_META } from "@/components/dashboard/TimelineDialog";

const PLACEHOLDER = "https://images.unsplash.com/photo-1581197326868-51cafbd1a169?crop=entropy&cs=srgb&fm=jpg&w=120&q=60";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  if (diff < 2592000) return `há ${Math.floor(diff / 86400)} d`;
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
}

function detailText(e) {
  const d = e.details || {};
  if (e.type === "mudou_cidade" && d.de) return `${d.de} → ${d.para}`;
  if (e.type === "mudou_telefone" && d.de) return `${d.de} → ${d.para}`;
  if (e.type === "reapareceu" && d.dias_ausente != null) return `ausente ${d.dias_ausente} dia(s)`;
  return null;
}

const PERIODS = [
  { k: 1, label: "24h" },
  { k: 7, label: "7 dias" },
  { k: 30, label: "30 dias" },
  { k: 0, label: "Tudo" },
];

export default function AlertsFeed({ sources = [] }) {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [type, setType] = useState("");
  const [source, setSource] = useState("");
  const [days, setDays] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const pageSize = 25;

  const loadSummary = useCallback(async () => {
    const { data } = await api.get("/alerts/summary", { params: { days: 7 } });
    setSummary(data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/alerts", {
        params: { type, source, days, page, page_size: pageSize },
      });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [type, source, days, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const win = summary?.window || {};
  const types = Object.keys(TYPE_META);

  return (
    <div className="space-y-6" data-testid="alerts-feed">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {types.map((t) => {
          const meta = TYPE_META[t];
          const Icon = meta.icon;
          const c24 = summary?.last_24h?.[t] || 0;
          const c7 = win[t] || 0;
          const selected = type === t;
          return (
            <button
              key={t}
              data-testid={`alert-card-${t}`}
              onClick={() => { setType(selected ? "" : t); setPage(1); }}
              className={`text-left border p-4 transition-colors ${selected ? "border-[#c8aa6e] bg-[#c8aa6e]/10" : "border-[#27272a] bg-[#18181b] hover:border-[#3f3f46]"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4" style={{ color: meta.color }} />
                <span className="label-tiny">{meta.label}</span>
              </div>
              <div className="text-2xl font-head text-[#fafafa] leading-none">{c7}</div>
              <div className="text-[10px] text-[#71717a] mt-1">{c24} nas últimas 24h · 7 dias</div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex border border-[#27272a]" data-testid="alerts-period">
          {PERIODS.map((p) => (
            <button
              key={p.k}
              onClick={() => { setDays(p.k); setPage(1); }}
              className={`px-3 py-2 text-xs transition-colors ${days === p.k ? "bg-[#c8aa6e] text-black" : "text-[#a1a1aa] hover:text-[#fafafa]"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <select
          data-testid="alerts-source-filter"
          value={source}
          onChange={(e) => { setSource(e.target.value); setPage(1); }}
          className="bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]"
        >
          <option value="">Todos os sites</option>
          {sources.map((s) => (
            <option key={s.key} value={s.key}>{s.name}</option>
          ))}
        </select>
        {type && (
          <button onClick={() => { setType(""); setPage(1); }} className="text-xs text-[#c8aa6e] hover:text-[#d4b982] underline">
            Limpar filtro de tipo
          </button>
        )}
        <span className="ml-auto text-xs text-[#71717a]">{total} eventos</span>
      </div>

      {/* Feed */}
      <div className="bg-[#18181b] border border-[#27272a] divide-y divide-[#27272a]">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-[#71717a] py-10 justify-center">
            <Loader2 className="w-4 h-4 spin" /> A carregar alertas…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="flex flex-col items-center gap-2 text-sm text-[#71717a] py-12">
            <Bell className="w-6 h-6 opacity-40" />
            Sem alertas para este filtro.
          </div>
        )}
        {!loading && items.map((e, i) => {
          const meta = TYPE_META[e.type] || { label: e.label, icon: Flag, color: "#a1a1aa" };
          const Icon = meta.icon;
          const dt = detailText(e);
          return (
            <div key={e.id} data-testid={`alert-row-${i}`} className="flex items-center gap-4 px-4 py-3 hover:bg-[#27272a]/40 transition-colors">
              <img
                src={imgProxy(e.photo_url, 160) || PLACEHOLDER}
                onError={(ev) => (ev.currentTarget.src = PLACEHOLDER)}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-10 h-10 object-cover grayscale-[20%] shrink-0 bg-[#27272a]"
              />
              <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border shrink-0" style={{ color: meta.color, borderColor: `${meta.color}55` }}>
                <Icon className="w-3.5 h-3.5" /> {meta.label}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#fafafa] font-medium truncate">{e.name || "—"}</div>
                <div className="text-xs text-[#71717a] truncate">
                  {e.source_name} · {e.city || "—"}
                  {dt && <span className="text-[#a1a1aa] font-mono"> · {dt}</span>}
                </div>
              </div>
              <span className="text-xs text-[#71717a] whitespace-nowrap shrink-0">{timeAgo(e.at)}</span>
              <button
                data-testid={`alert-timeline-${i}`}
                onClick={() => setActive({ id: e.profile_id, name: e.name, city: e.city, source_name: e.source_name, photo_url: e.photo_url, profile_url: e.profile_url })}
                disabled={!e.profile_id}
                title="Ver histórico do perfil"
                className="text-[#a1a1aa] hover:text-[#c8aa6e] transition-colors disabled:opacity-30 shrink-0"
              >
                <History className="w-4 h-4" />
              </button>
              {e.profile_url && (
                <a href={e.profile_url} target="_blank" rel="noreferrer" className="text-[#c8aa6e] hover:text-[#d4b982] shrink-0">
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm text-[#a1a1aa]">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 border border-[#3f3f46] disabled:opacity-30 hover:border-[#c8aa6e]" data-testid="alerts-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-mono text-xs">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1.5 border border-[#3f3f46] disabled:opacity-30 hover:border-[#c8aa6e]" data-testid="alerts-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <TimelineDialog
        profile={active}
        open={!!active}
        onOpenChange={(o) => !o && setActive(null)}
      />
    </div>
  );
}
